import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Star, X, ArrowRight, Bell, Mail, Slack } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthProvider';
import SyncBar from '../components/SyncBar';
import PetanqueLoader from '../components/PetanqueLoader';
import SupplierDetailDialog from '../components/SupplierDetailDialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { money, formatNumber, BLOCKED } from '../lib/format';
import { cn } from '../lib/utils';
import usePollWhileActive from '../hooks/usePollWhileActive';

const PREFS = [
  ['on_payment', 'Payments'],
  ['on_changes', 'Changes'],
  ['on_status', 'Status'],
  ['on_note', 'Notes'],
];

// WHERE to deliver. In-app (the bell) always records, so it's shown locked-on;
// email + Slack are the toggleable push channels.
const CHANNELS = [
  ['via_email', 'Email', Mail],
  ['via_slack', 'Slack', Slack],
];

const FOLLOW_DEFAULTS = {
  on_payment: true, on_changes: true, on_status: true, on_note: false,
  via_email: false, via_slack: true,   // email off by default (too spammy)
};

export default function Following() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const data = await api.suppliers.list({ filter: 'following', all: 'true', ordering: 'status' });
      setRows(data.results || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.users.list().then(setUsers).catch(() => {}); }, []);
  usePollWhileActive(true, () => load(true), 30000);

  const setPref = async (sup, key, val) => {
    const prefs = { ...FOLLOW_DEFAULTS, ...(sup.my_follow || {}), [key]: val };
    setRows((rs) => rs.map((r) => (r.id === sup.id ? { ...r, my_follow: prefs } : r)));  // optimistic
    try { await api.suppliers.follow(sup.id, prefs); } catch { load(true); }
  };
  const unfollow = async (sup) => {
    setRows((rs) => rs.filter((r) => r.id !== sup.id));  // optimistic
    try { await api.suppliers.unfollow(sup.id); } catch { load(true); }
  };

  return (
    <AppLayout
      title="Following"
      description="Vendors you've spotlighted - manage what you're notified about"
      syncBar={<SyncBar onSynced={() => load(true)} />}
    >
      {loading ? (
        <div className="grid h-64 place-items-center"><PetanqueLoader size={38} roll /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center shadow-card">
          <Star className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <div className="text-[15px] font-medium">You're not following any vendors yet</div>
          <div className="mt-1 text-[13px] text-muted-foreground">
            Open a vendor and hit <span className="font-medium">Follow</span> to get notified about payments, changes and more.
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2">
            <Star className="h-4 w-4 text-primary" />
            <span className="text-[15px] font-semibold">You follow {formatNumber(rows.length)} vendor{rows.length === 1 ? '' : 's'}</span>
            <span className="text-[12px] text-muted-foreground">tap a tag to toggle that notification</span>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
            <Table className="min-w-[880px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Vendor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notify me about</TableHead>
                  <TableHead>Deliver to</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                  <TableHead className="text-right"> </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => {
                  const blk = BLOCKED[s.account_status] || BLOCKED[1];
                  const my = s.my_follow || {};
                  return (
                    <TableRow key={s.id} className="hover:bg-muted/30">
                      <TableCell>
                        <button onClick={() => navigate(`/following/${s.id}`)}
                          className="group inline-flex items-center gap-1 text-left font-medium hover:text-primary">
                          {s.name || '-'}
                          <ArrowRight className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" />
                        </button>
                        {s.follower_count > 1 && (
                          <div className="text-[11px] text-muted-foreground">+{s.follower_count - 1} other follower{s.follower_count - 1 === 1 ? '' : 's'}</div>
                        )}
                      </TableCell>
                      <TableCell><Badge variant={blk.variant}>{blk.label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {PREFS.map(([k, lbl]) => (
                            <button key={k} onClick={() => setPref(s, k, !my[k])}
                              className={cn('rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors',
                                my[k] ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <span title="The in-app bell always records your notifications."
                            className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/12 px-2 py-0.5 text-[11px] font-medium text-foreground">
                            <Bell className="h-3 w-3" /> In-app
                          </span>
                          {CHANNELS.map(([k, lbl, Icon]) => {
                            const on = my[k] !== false;   // default on (older follows have no channel prefs yet)
                            const slackOff = k === 'via_slack' && user?.slack_enabled === false;
                            return (
                              <button key={k} onClick={() => setPref(s, k, !on)}
                                title={slackOff ? "Slack isn't connected yet - this won't deliver until an admin sets it up." : undefined}
                                className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors',
                                  on ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
                                <Icon className="h-3 w-3" /> {lbl}
                                {slackOff && on && <span className="text-[10px] text-amber-600">·off</span>}
                              </button>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold tnum">
                        {s.overdue_amount ? <span className="text-rose-600">{money(s.overdue_amount)}</span> : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => unfollow(s)} title="Stop following"
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-rose-600">
                          <X className="h-3.5 w-3.5" /> Unfollow
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <SupplierDetailDialog supplierId={id} open={!!id}
                            onClose={() => navigate('/following')}
                            onChanged={() => load(true)} users={users} />
    </AppLayout>
  );
}
